import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';

import { ClickableArea } from '../base/Button';
import { StyledCard, StyledCardSection } from '../base/Card';
import Divider from '../base/Divider';
import Text from '../base/Text';
import ScrollArea from '../helpers/ScrollArea';
import { Div } from '../helpers/StyledUtils';
import Search from '../Search';
import { ChartDataSet, ChartHoveredState } from './Chart';
import { formatChartNumber } from './format';
import LegendColor from './LegendColor';

interface ChartTooltipProps {
  label: string | number;
  mono: boolean;
  data: [string, string | number][];
  datasets: ChartDataSet[];
  hovered?: string;
  sort_hovered?: boolean;
  locked?: boolean;

  setHover: Dispatch<SetStateAction<ChartHoveredState | null>>;
  setLockedState: Dispatch<SetStateAction<{ hovered?: ChartHoveredState | null }>>;
}

const filterData = (data: [string, string | number][], search_term: string) => {
  if (!search_term) {
    return data;
  }

  const filtered_data = data.reduce((prev: [string, string | number][], [k, v]) => {
    if (k.toLowerCase().includes(search_term.toLowerCase())) {
      prev.push([k, v]);
    }

    return prev;
  }, []);

  return filtered_data;
};

const ChartTooltip: React.FC<ChartTooltipProps> = ({
  label,
  data,
  mono,
  datasets,
  hovered,
  sort_hovered = false,
  locked = false,
  setHover,
  setLockedState,
}) => {
  const theme = useTheme();
  const [filtered_data, setFilteredData] = useState(data);
  const [search_term, setSearchTerm] = useState('');

  useEffect(() => {
    setFilteredData(filterData(data, search_term));
  }, [data]);

  const sorted_data = (() => {
    if (!hovered || !sort_hovered) {
      return filtered_data;
    }

    const hovered_dataset = datasets.find((dataset) => dataset.key === hovered);
    if (!hovered_dataset) {
      return filtered_data;
    }

    return [...filtered_data].sort(([a], [b]) => {
      if (a === hovered_dataset.label) {
        return -1;
      }
      if (b === hovered_dataset.label) {
        return 1;
      }
      return 0;
    });
  })();

  const handleSearch = (value) => {
    const filtered_data = filterData(data, value);

    if (value) {
      if (!filtered_data.length) {
        return;
      }
      const dataset = datasets.find((d) => d.label === filtered_data[0][0]);
      if (!dataset) {
        return;
      }
      const hovered_state = {
        key: dataset.key,
        label: dataset.label,
      };
      setHover(hovered_state);
      setLockedState((prev) => ({
        ...(prev || {}),
        hovered: hovered_state,
        searched: true,
      }));
    } else {
      setLockedState((prev) => ({
        ...(prev || {}),
        hovered: null,
        searched: false,
      }));
    }

    setSearchTerm(value);
    setFilteredData(filtered_data);
  };

  return (
    <StyledCard
      raised
      style={{ pointerEvents: locked ? 'auto' : 'none', overflow: 'hidden' }}
      min_w={{ px: 248 }}
      onClick={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}>
      <StyledCardSection p={{ x: 3, y: 2 }}>
        <Text bold>{label}</Text>
        {(search_term || filtered_data?.length > 4) && (
          <Search small m={{ t: 2 }} focus value={search_term} onChange={handleSearch} />
        )}
      </StyledCardSection>
      <Divider />
      <ScrollArea max_h={{ px: 196 }} p={1}>
        {sorted_data.map(([k, value], i) => {
          const dataset = datasets.find((d) => d.label === k);
          // `filtered_data` is state synced from `data` one render late, so while datasets
          // reload (e.g. metrics polling with the tooltip open) a row's label can briefly
          // have no dataset. Skip it; the effect above re-syncs on the next render.
          if (!dataset) {
            return null;
          }
          const is_hovered = hovered === dataset.key;
          return (
            <ClickableArea
              as="div"
              key={`${k}${i}`}
              highlight={is_hovered}
              rounded
              flex={{ justify: 'space-between', align: 'center' }}
              p={{ x: 2, y: 1 }}
              onMouseMove={(e) => {
                if (!locked) {
                  return;
                }
                e.stopPropagation();
              }}
              onMouseEnter={(e) => {
                if (!locked) {
                  return;
                }
                e.stopPropagation();
                setHover({
                  key: dataset.key,
                  label: dataset.label,
                  tooltip: true,
                  legend: true,
                });
              }}
              onMouseLeave={() => setHover(null)}>
              <Div flex={{ align: 'center' }}>
                <LegendColor
                  color={
                    dataset.theme_color
                      ? theme.colors.surface.chart[dataset.theme_color]
                      : dataset.hex_color
                  }
                  m={{ r: 2 }}
                />
                <Text
                  text_wrap={false}
                  mono={mono}
                  semi={!mono}
                  size="s"
                  max_w={{ px: 200 }}
                  ellipsis
                  dark={is_hovered}
                  muted={hovered ? !is_hovered : false}>
                  {k}
                </Text>
              </Div>
              <Text
                text_wrap={false}
                dark={is_hovered}
                muted={hovered ? !is_hovered : false}
                m={{ l: 4 }}>
                {isNaN(parseFloat(String(value)))
                  ? 'N/A'
                  : (dataset?.getDataLabel?.(value) ?? formatChartNumber(value))}
              </Text>
            </ClickableArea>
          );
        })}
      </ScrollArea>
    </StyledCard>
  );
};

export default ChartTooltip;
